import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Calendar,
  ChevronDown,
  Video,
  Clock,
  Users,
  ExternalLink,
  Linkedin,
  Building2,
  Building,
  Plus,
  RefreshCw,
  Play,
  FileVideo,
  FileText,
  Loader2,
  CalendarDays,
  Zap,
  Link2,
  Unlink,
  Briefcase,
  History,
  Lightbulb,
  ChevronRight,
  User,
  UserPlus,
  Target,
  MessageSquare,
  HelpCircle,
  AlertTriangle,
  ArrowRight
} from "lucide-react";

const PrioritiesSection = ({ userId, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefsLoading, setBriefsLoading] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState(null);
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [creatingMeeting, setCreatingMeeting] = useState(false);
  const [connectingOutlook, setConnectingOutlook] = useState(false);
  const [expandedBrief, setExpandedBrief] = useState(null);
  const [addingAttendee, setAddingAttendee] = useState(null); // meeting_id being edited
  const [newAttendeeEmail, setNewAttendeeEmail] = useState("");

  useEffect(() => {
    fetchCalendarData();
    fetchIntegrationStatus();
    fetchOutlookStatus();
    
    // Check for Outlook callback params in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get("outlook_connected") === "true") {
      toast.success("Outlook Calendar connected successfully!");
      fetchOutlookStatus();
      fetchCalendarData();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get("outlook_error")) {
      toast.error("Failed to connect Outlook Calendar. Please try again.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [userId]);

  const fetchIntegrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/status`);
      setIntegrationStatus(response.data);
    } catch (error) {
      console.error("Error fetching integration status:", error);
    }
  };

  const fetchOutlookStatus = async () => {
    if (!userId) return;
    try {
      const response = await axios.get(`${API}/calendar/outlook/status/${userId}`);
      setOutlookStatus(response.data);
    } catch (error) {
      console.error("Error fetching Outlook status:", error);
    }
  };

  const fetchCalendarData = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      // Fetch from all sources - always try Outlook since API handles auth
      const [eventsRes, recordingsRes, outlookRes] = await Promise.all([
        axios.get(`${API}/calendar/events/${userId}?hours=168`).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/zoom/recordings`).catch(() => ({ data: { recordings: [] } })),
        axios.get(`${API}/calendar/outlook/events/${userId}?days=7`).catch(() => ({ data: { events: [] } }))
      ]);
      
      let allMeetings = eventsRes.data.events || [];
      
      // Merge Outlook events
      const outlookEvents = outlookRes.data.events || [];
      if (outlookEvents.length > 0) {
        const existingIds = new Set(allMeetings.map(m => m.source_id || m.id));
        outlookEvents.forEach(event => {
          if (!existingIds.has(event.source_id) && !existingIds.has(event.id)) {
            allMeetings.push(event);
          }
        });
      }
      
      // Sort by start time
      allMeetings.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      
      setMeetings(allMeetings);
      setRecordings(recordingsRes.data.recordings || []);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      setMeetings([]);
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectOutlook = async () => {
    setConnectingOutlook(true);
    try {
      const response = await axios.get(`${API}/calendar/outlook/connect/${userId}`);
      if (response.data.authorization_url) {
        // Redirect to Microsoft login
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      console.error("Error connecting Outlook:", error);
      toast.error("Failed to initiate Outlook connection");
      setConnectingOutlook(false);
    }
  };

  const handleDisconnectOutlook = async () => {
    try {
      await axios.delete(`${API}/calendar/outlook/disconnect/${userId}`);
      toast.success("Outlook Calendar disconnected");
      setOutlookStatus({ connected: false });
      fetchCalendarData();
    } catch (error) {
      console.error("Error disconnecting Outlook:", error);
      toast.error("Failed to disconnect Outlook");
    }
  };

  const fetchBriefs = async () => {
    if (!userId) return;
    setBriefsLoading(true);
    try {
      const response = await axios.get(`${API}/calendar/briefs/${userId}?days=7`);
      setBriefs(response.data.briefs || []);
    } catch (error) {
      console.error("Error fetching briefs:", error);
      setBriefs([]);
    } finally {
      setBriefsLoading(false);
    }
  };

  // Fetch briefs when tab changes to briefs
  useEffect(() => {
    if (activeTab === "briefs" && briefs.length === 0) {
      fetchBriefs();
    }
  }, [activeTab]);

  const handleAddAttendee = async (meetingId) => {
    if (!newAttendeeEmail || !newAttendeeEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      // Get existing attendees for this meeting
      const meeting = meetings.find(m => m.id === meetingId);
      const existingAttendees = meeting?.attendees || [];
      
      // Add new attendee
      const newAttendee = { email: newAttendeeEmail };
      const allAttendees = [...existingAttendees, newAttendee];

      await axios.post(`${API}/calendar/meeting/${meetingId}/attendees?user_id=${userId}`, allAttendees);
      
      toast.success("Attendee added! Refresh to generate brief.");
      setNewAttendeeEmail("");
      setAddingAttendee(null);
      
      // Refresh data
      fetchCalendarData();
      setBriefs([]); // Clear briefs to force regeneration
    } catch (error) {
      console.error("Error adding attendee:", error);
      toast.error("Failed to add attendee");
    }
  };

  const handleJoinMeeting = (meetingLink) => {
    if (meetingLink?.url) {
      window.open(meetingLink.url, "_blank");
    } else {
      toast.error("No meeting link available");
    }
  };

  const handleCreateMeeting = async () => {
    setCreatingMeeting(true);
    try {
      const response = await axios.post(`${API}/zoom/meetings`, {
        topic: "Quick Sales Call",
        duration: 30,
        description: "Created from ISM dashboard"
      });
      
      if (response.data.success) {
        toast.success("Zoom meeting created!");
        navigator.clipboard.writeText(response.data.meeting.join_url);
        toast.info("Meeting link copied to clipboard");
        fetchCalendarData();
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast.error("Failed to create meeting");
    } finally {
      setCreatingMeeting(false);
    }
  };

  const handleViewRecording = (recording) => {
    if (recording.share_url) {
      window.open(recording.share_url, "_blank");
    } else if (recording.play_url) {
      window.open(recording.play_url, "_blank");
    } else {
      toast.error("Recording not available");
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTimeUntilMeeting = (startTime) => {
    if (!startTime) return "";
    const now = new Date();
    const start = new Date(startTime);
    const diffMs = start - now;
    
    if (diffMs < 0) return "Started";
    
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `in ${diffMins}m`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `in ${diffHours}h`;
    
    return formatDate(startTime);
  };

  const getMeetingTypeIcon = (source) => {
    switch (source) {
      case "zoom":
        return <Video className="h-4 w-4 text-blue-400" />;
      case "google":
        return <Video className="h-4 w-4 text-neon-green" />;
      case "outlook":
        return <Video className="h-4 w-4 text-blue-500" />;
      default:
        return <Calendar className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <motion.div 
      className="glass-card card-3d cursor-pointer overflow-hidden relative"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      onClick={() => setIsExpanded(!isExpanded)}
      data-testid="priorities-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, height: isExpanded ? 600 : 140 }}
      transition={{ duration: 0.4, ease: "easeInOut" }}
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" />

      <div className="p-6 relative z-10">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-neon-cyan/20 to-neon-cyan/10 border border-neon-cyan/20">
                <CalendarDays className="h-6 w-6 text-neon-cyan" />
              </div>
              <div>
                <h2 className="text-xl text-white tracking-tight flex items-center gap-2">
                  <span className="text-neon-cyan">Calendar & Meetings</span>
                  {meetings.length > 0 && (
                    <div className="px-2 py-0.5 rounded-full bg-neon-cyan/20 border border-neon-cyan/30">
                      <span className="text-xs text-neon-cyan font-medium">{meetings.length}</span>
                    </div>
                  )}
                </h2>
                <p className="text-sm text-gray-500">
                  {isExpanded 
                    ? (meetings.length > 0 ? `${meetings.length} upcoming in next 24h` : "No upcoming meetings") 
                    : `${meetings.length} meetings today`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <AnimatePresence>
                {isExpanded && (
                  <>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                    >
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchCalendarData();
                        }}
                        size="sm"
                        className="bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 hover:text-white h-8 px-3 rounded-lg"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateMeeting();
                        }}
                        disabled={creatingMeeting}
                        size="sm"
                        className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white h-8 px-3 rounded-lg border-0"
                      >
                        {creatingMeeting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Video className="h-4 w-4 mr-1" />
                            Create Zoom
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="p-1.5 rounded-lg bg-white/5"
              >
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              className="flex gap-2 mb-4" 
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <motion.button
                onClick={() => setActiveTab("upcoming")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all ${
                  activeTab === "upcoming" 
                    ? "bg-gradient-to-r from-neon-cyan/20 to-blue-500/20 text-neon-cyan border border-neon-cyan/30" 
                    : "bg-white/5 text-gray-400 hover:text-white border border-white/10"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Calendar className="h-4 w-4" />
                Upcoming ({meetings.length})
              </motion.button>
              <motion.button
                onClick={() => setActiveTab("recordings")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all ${
                  activeTab === "recordings" 
                    ? "bg-gradient-to-r from-neon-pink/20 to-purple-500/20 text-neon-pink border border-neon-pink/30" 
                    : "bg-white/5 text-gray-400 hover:text-white border border-white/10"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FileVideo className="h-4 w-4" />
                Recordings ({recordings.length})
              </motion.button>
              <motion.button
                onClick={() => setActiveTab("briefs")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all ${
                  activeTab === "briefs" 
                    ? "bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border border-amber-500/30" 
                    : "bg-white/5 text-gray-400 hover:text-white border border-white/10"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FileText className="h-4 w-4" />
                Briefs
              </motion.button>
              <motion.button
                onClick={() => setActiveTab("connect")}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-all ${
                  activeTab === "connect" 
                    ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 border border-blue-500/30" 
                    : "bg-white/5 text-gray-400 hover:text-white border border-white/10"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link2 className="h-4 w-4" />
                Connect
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <ScrollArea className="pr-4" style={{ height: 420 }}>
                {loading ? (
                  <div className="text-center py-12">
                    <motion.div 
                      className="w-12 h-12 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full mx-auto mb-4"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    <p className="text-gray-500 text-sm">Loading calendar...</p>
                  </div>
                ) : activeTab === "upcoming" ? (
                  meetings.length > 0 ? (
                    <div className="space-y-3">
                      {meetings.map((meeting, idx) => (
                        <MeetingCard 
                          key={meeting.id || idx}
                          meeting={meeting}
                          index={idx}
                          formatTime={formatTime}
                          formatDate={formatDate}
                          getTimeUntilMeeting={getTimeUntilMeeting}
                          getMeetingTypeIcon={getMeetingTypeIcon}
                          onJoin={handleJoinMeeting}
                          isAddingAttendee={addingAttendee === meeting.id}
                          onStartAddAttendee={() => setAddingAttendee(meeting.id)}
                          onCancelAddAttendee={() => { setAddingAttendee(null); setNewAttendeeEmail(""); }}
                          newAttendeeEmail={newAttendeeEmail}
                          onNewAttendeeEmailChange={setNewAttendeeEmail}
                          onAddAttendee={() => handleAddAttendee(meeting.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <motion.div 
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-neon-cyan/10 border border-white/10 inline-block mb-4">
                        <Calendar className="h-12 w-12 text-blue-400" />
                      </div>
                      <p className="text-gray-400 mb-3">No upcoming meetings</p>
                      <p className="text-gray-600 text-xs mb-4">
                        {outlookStatus?.connected 
                          ? "Your Outlook is connected. Meetings will appear here." 
                          : "Connect your Outlook calendar to see meetings."}
                      </p>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!outlookStatus?.connected) {
                              handleConnectOutlook();
                            } else {
                              handleCreateMeeting();
                            }
                          }}
                          disabled={creatingMeeting || connectingOutlook}
                          size="sm"
                          className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg"
                        >
                          {!outlookStatus?.connected ? (
                            <>
                              <Link2 className="h-4 w-4 mr-2" />
                              Connect Outlook
                            </>
                          ) : (
                            <>
                              <Video className="h-4 w-4 mr-2" />
                              Create Zoom Meeting
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </motion.div>
                  )
                ) : activeTab === "recordings" ? (
                  recordings.length > 0 ? (
                    <div className="space-y-3">
                      {recordings.map((recording, idx) => (
                        <RecordingCard 
                          key={recording.uuid || idx}
                          recording={recording}
                          index={idx}
                          onView={handleViewRecording}
                          formatDate={formatDate}
                        />
                      ))}
                    </div>
                  ) : (
                    <motion.div 
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="p-6 rounded-2xl bg-gradient-to-br from-neon-pink/10 to-purple-500/10 border border-white/10 inline-block mb-4">
                        <FileVideo className="h-12 w-12 text-neon-pink" />
                      </div>
                      <p className="text-gray-400 mb-3">No recordings available</p>
                      <p className="text-gray-600 text-xs">
                        Meeting recordings will appear here after your Zoom calls.
                      </p>
                    </motion.div>
                  )
                ) : activeTab === "briefs" ? (
                  briefsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
                      <p className="text-gray-500 text-sm ml-3">Generating meeting briefs...</p>
                    </div>
                  ) : briefs.length > 0 ? (
                    <div className="space-y-4">
                      {briefs.map((brief, idx) => (
                        <MeetingBriefCard
                          key={brief.meeting_id || idx}
                          brief={brief}
                          index={idx}
                          formatTime={formatTime}
                          formatDate={formatDate}
                          isExpanded={expandedBrief === brief.meeting_id}
                          onToggle={() => setExpandedBrief(expandedBrief === brief.meeting_id ? null : brief.meeting_id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <motion.div 
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <div className="p-6 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-white/10 inline-block mb-4">
                        <FileText className="h-12 w-12 text-amber-400" />
                      </div>
                      <p className="text-gray-400 mb-3">No meeting briefs available</p>
                      <p className="text-gray-600 text-xs mb-4">
                        {outlookStatus?.connected 
                          ? "Briefs will appear when you have upcoming meetings." 
                          : "Connect your calendar to generate meeting briefs."}
                      </p>
                      {!outlookStatus?.connected && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConnectOutlook();
                          }}
                          size="sm"
                          className="bg-gradient-to-r from-amber-600 to-orange-500 hover:from-amber-500 hover:to-orange-400 text-white rounded-lg"
                        >
                          <Link2 className="h-4 w-4 mr-2" />
                          Connect Calendar
                        </Button>
                      )}
                    </motion.div>
                  )
                ) : activeTab === "connect" ? (
                  <motion.div 
                    className="space-y-4 py-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    {/* Outlook Connection */}
                    <div className="glass-card-light rounded-xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                            <svg className="h-6 w-6 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-white text-sm">Microsoft Outlook</h3>
                            <p className="text-gray-500 text-xs">
                              {outlookStatus?.connected 
                                ? `Connected: ${outlookStatus.outlook_email}` 
                                : "Sync your Outlook calendar events"}
                            </p>
                          </div>
                        </div>
                        
                        {outlookStatus?.connected ? (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDisconnectOutlook();
                            }}
                            size="sm"
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg"
                          >
                            <Unlink className="h-4 w-4 mr-1" />
                            Disconnect
                          </Button>
                        ) : (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConnectOutlook();
                            }}
                            disabled={connectingOutlook}
                            size="sm"
                            className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 rounded-lg"
                          >
                            {connectingOutlook ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <Link2 className="h-4 w-4 mr-1" />
                            )}
                            Connect
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Zoom Status */}
                    <div className="glass-card-light rounded-xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-blue-600/10 border border-blue-600/20">
                            <Video className="h-6 w-6 text-blue-500" />
                          </div>
                          <div>
                            <h3 className="text-white text-sm">Zoom</h3>
                            <p className="text-gray-500 text-xs">
                              {integrationStatus?.zoom?.configured 
                                ? "Connected - Server to Server OAuth" 
                                : "Not configured"}
                            </p>
                          </div>
                        </div>
                        
                        <Badge className={`${
                          integrationStatus?.zoom?.configured 
                            ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                            : "bg-gray-500/20 text-gray-400 border-gray-500/30"
                        }`}>
                          {integrationStatus?.zoom?.configured ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Google Calendar - Coming Soon */}
                    <div className="glass-card-light rounded-xl p-5 opacity-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                            <Calendar className="h-6 w-6 text-red-400" />
                          </div>
                          <div>
                            <h3 className="text-white text-sm">Google Calendar</h3>
                            <p className="text-gray-500 text-xs">Coming soon</p>
                          </div>
                        </div>
                        
                        <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                          Coming Soon
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                ) : null}
              </ScrollArea>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Meeting Card Component with Cyber-Glass styling
const MeetingCard = ({ 
  meeting, index, formatTime, formatDate, getTimeUntilMeeting, getMeetingTypeIcon, onJoin,
  isAddingAttendee, onStartAddAttendee, onCancelAddAttendee, newAttendeeEmail, onNewAttendeeEmailChange, onAddAttendee
}) => {
  const timeUntil = getTimeUntilMeeting(meeting.start_time);
  const isStartingSoon = timeUntil.includes("m") && parseInt(timeUntil) <= 15;
  const meetingLink = meeting.meeting_links?.[0];
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.01, x: 4 }}
      className={`glass-card-light rounded-xl p-4 transition-all ${
        isStartingSoon ? 'border-neon-green/30 shadow-[0_0_20px_rgba(0,255,148,0.1)]' : ''
      }`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`meeting-card-${meeting.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {getMeetingTypeIcon(meeting.source)}
          <span className="text-white text-sm">{meeting.title}</span>
        </div>
        <Badge className={`text-xs ${
          isStartingSoon 
            ? "bg-neon-green/20 text-neon-green border-neon-green/30" 
            : "bg-white/10 text-gray-400 border-white/10"
        }`}>
          {isStartingSoon && <Zap className="h-3 w-3 mr-1" />}
          {timeUntil}
        </Badge>
      </div>
      
      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatDate(meeting.start_time)} at {formatTime(meeting.start_time)}
        </div>
        {meeting.duration_minutes && (
          <span className="text-gray-600">{meeting.duration_minutes} min</span>
        )}
      </div>

      {/* Attendees Section */}
      {meeting.attendees && meeting.attendees.length > 0 ? (
        <div className="mb-3">
          <div className="text-xs text-gray-600 mb-1.5 flex items-center gap-1">
            <Users className="h-3 w-3" />
            Meeting With
          </div>
          <div className="flex flex-wrap gap-2">
            {meeting.attendees.slice(0, 5).map((attendee, idx) => (
              <motion.div 
                key={idx} 
                className="flex items-center gap-1.5 bg-blue-500/10 rounded-full px-2.5 py-1 border border-blue-500/20"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: idx * 0.05 }}
              >
                <User className="h-3 w-3 text-blue-400" />
                <span className="text-xs text-gray-200">{attendee.name || attendee.email.split('@')[0]}</span>
                {attendee.company && (
                  <span className="text-xs text-blue-400 flex items-center gap-0.5">
                    @ {attendee.company}
                  </span>
                )}
              </motion.div>
            ))}
            {meeting.attendees.length > 5 && (
              <span className="text-xs text-gray-500">+{meeting.attendees.length - 5} more</span>
            )}
          </div>
        </div>
      ) : isAddingAttendee ? (
        <div className="mb-3 space-y-2">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Enter attendee email (e.g., john@company.com)"
              value={newAttendeeEmail}
              onChange={(e) => onNewAttendeeEmailChange(e.target.value)}
              className="flex-1 bg-white/5 border-white/20 text-white text-xs h-8"
              onKeyDown={(e) => e.key === 'Enter' && onAddAttendee()}
            />
            <Button
              size="sm"
              onClick={onAddAttendee}
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 h-8 text-xs"
            >
              Add
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onCancelAddAttendee}
              className="text-gray-400 hover:text-white h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
          <p className="text-xs text-gray-500">Add attendee to generate AI intelligence brief</p>
        </div>
      ) : (
        <div className="mb-3">
          <button
            onClick={onStartAddAttendee}
            className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors"
          >
            <UserPlus className="h-3 w-3" />
            Add attendee for AI brief
          </button>
        </div>
      )}

      <div className="flex items-center gap-2">
        {meetingLink && (
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={() => onJoin(meetingLink)}
              size="sm"
              className={`${
                isStartingSoon 
                  ? "bg-gradient-to-r from-neon-green to-neon-green/80 text-black font-semibold shadow-[0_0_20px_rgba(0,255,148,0.3)]" 
                  : "bg-gradient-to-r from-blue-600 to-blue-500 text-white"
              } text-xs h-7 rounded-lg border-0`}
            >
              <Play className="h-3 w-3 mr-1" />
              Join {meetingLink.type === "zoom" ? "Zoom" : "Meeting"}
            </Button>
          </motion.div>
        )}
        {meeting.is_sales_meeting && (
          <Badge className="bg-neon-pink/20 text-neon-pink border-neon-pink/30 text-xs">
            Sales Meeting
          </Badge>
        )}
      </div>
    </motion.div>
  );
};

// Recording Card Component with Cyber-Glass styling
const RecordingCard = ({ recording, index, onView, formatDate }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ scale: 1.01, x: 4 }}
      className="glass-card-light rounded-xl p-4 hover-glow-pink"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileVideo className="h-4 w-4 text-neon-pink" />
          <span className="text-white text-sm">{recording.topic}</span>
        </div>
        <span className="text-xs text-gray-500">
          {formatDate(recording.start_time)}
        </span>
      </div>
      
      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
        <span>{recording.duration ? `${Math.round(recording.duration / 60)} min` : ""}</span>
        {recording.total_size && (
          <span>{(recording.total_size / 1024 / 1024).toFixed(1)} MB</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button
            onClick={() => onView(recording)}
            size="sm"
            className="bg-gradient-to-r from-neon-pink to-neon-pink/80 hover:from-neon-pink/90 hover:to-neon-pink/70 text-white text-xs h-7 rounded-lg border-0"
          >
            <Play className="h-3 w-3 mr-1" />
            Watch Recording
          </Button>
        </motion.div>
        {recording.recording_files?.some(f => f.recording_type === "audio_transcript") && (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
            Has Transcript
          </Badge>
        )}
      </div>
    </motion.div>
  );
};

// Meeting Brief Card Component - AI-powered intelligence for meetings
const MeetingBriefCard = ({ brief, index, formatTime, formatDate, isExpanded, onToggle }) => {
  const startTime = brief.start_time ? new Date(brief.start_time) : null;
  const isToday = startTime && new Date().toDateString() === startTime.toDateString();
  const hasAIContent = brief.executive_summary || brief.consultative_pov || brief.talking_points?.length > 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="glass-card-light rounded-xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header - Always Visible */}
      <div 
        className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {isToday && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                  Today
                </Badge>
              )}
              {hasAIContent && (
                <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                  <Zap className="h-3 w-3 mr-1" />
                  AI Brief Ready
                </Badge>
              )}
              <span className="text-xs text-gray-500">
                {formatDate(brief.start_time)} at {formatTime(brief.start_time)}
              </span>
            </div>
            <h3 className="text-white text-sm mb-2">{brief.title}</h3>
            
            {/* Executive Summary Preview */}
            {brief.executive_summary && !isExpanded && (
              <p className="text-gray-400 text-xs line-clamp-2 mb-2">
                {brief.executive_summary}
              </p>
            )}
            
            {/* Quick Stats */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {brief.attendees?.length > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {brief.attendees.length} attendee{brief.attendees.length !== 1 ? 's' : ''}
                </span>
              )}
              {brief.companies?.length > 0 && (
                <span className="flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {brief.companies.map(c => c.name).join(', ')}
                </span>
              )}
              {brief.previous_meeting_count > 0 && (
                <span className="flex items-center gap-1 text-emerald-400">
                  <History className="h-3 w-3" />
                  {brief.previous_meeting_count} previous
                </span>
              )}
              {brief.deal_strategy && (
                <span className="flex items-center gap-1 text-amber-400">
                  <Target className="h-3 w-3" />
                  {brief.deal_strategy.split(' ').slice(0, 2).join(' ')}
                </span>
              )}
            </div>
          </div>
          
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </motion.div>
        </div>
      </div>
      
      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-white/10"
          >
            <div className="p-4 space-y-4">
              
              {/* Executive Summary */}
              {brief.executive_summary && (
                <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-lg p-3">
                  <h4 className="text-xs text-purple-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    Executive Summary
                  </h4>
                  <p className="text-sm text-gray-200">{brief.executive_summary}</p>
                </div>
              )}
              
              {/* Consultative POV */}
              {brief.consultative_pov && (
                <div>
                  <h4 className="text-xs text-cyan-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Lightbulb className="h-3 w-3" />
                    Your Consultative Point of View
                  </h4>
                  <p className="text-sm text-gray-300 bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                    {brief.consultative_pov}
                  </p>
                </div>
              )}
              
              {/* Deal Strategy */}
              {brief.deal_strategy && (
                <div>
                  <h4 className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Target className="h-3 w-3" />
                    Recommended Strategy
                  </h4>
                  <p className="text-sm text-gray-300 bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                    {brief.deal_strategy}
                  </p>
                </div>
              )}
              
              {/* Talking Points */}
              {brief.talking_points?.length > 0 && (
                <div>
                  <h4 className="text-xs text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Key Talking Points
                  </h4>
                  <div className="space-y-2">
                    {brief.talking_points.map((point, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-300 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2">
                        <span className="text-emerald-400 mt-0.5">{i + 1}.</span>
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Questions to Ask */}
              {brief.questions_to_ask?.length > 0 && (
                <div>
                  <h4 className="text-xs text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <HelpCircle className="h-3 w-3" />
                    Discovery Questions
                  </h4>
                  <div className="space-y-2">
                    {brief.questions_to_ask.map((question, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className="text-blue-400">?</span>
                        <span>{question}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Risks & Objections */}
              {brief.risks_and_objections?.length > 0 && (
                <div>
                  <h4 className="text-xs text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Prepare For
                  </h4>
                  <div className="space-y-2">
                    {brief.risks_and_objections.map((risk, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-300 bg-red-500/5 border border-red-500/20 rounded-lg p-2">
                        <span className="text-red-400">!</span>
                        <span>{risk}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Next Steps */}
              {brief.next_steps_recommendation && (
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-lg p-3">
                  <h4 className="text-xs text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <ArrowRight className="h-3 w-3" />
                    Recommended Next Step
                  </h4>
                  <p className="text-sm text-gray-200">{brief.next_steps_recommendation}</p>
                </div>
              )}
              
              {/* Attendee Intelligence - Research results */}
              {brief.attendee_intelligence?.length > 0 && (
                <div>
                  <h4 className="text-xs text-violet-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    Person Intelligence
                  </h4>
                  <div className="space-y-3">
                    {brief.attendee_intelligence.map((intel, i) => (
                      <div key={i} className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-violet-300">{intel.name}</span>
                          {intel.linkedin_url && (
                            <a 
                              href={intel.linkedin_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              LinkedIn
                            </a>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 block mb-1">@ {intel.company}</span>
                        {intel.background && (
                          <p className="text-xs text-gray-400 mt-1">{intel.background}</p>
                        )}
                        {intel.role_context && !intel.background && (
                          <p className="text-xs text-gray-400 mt-1">{intel.role_context}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Company Intelligence - Research results */}
              {brief.company_intelligence?.length > 0 && (
                <div>
                  <h4 className="text-xs text-indigo-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    Company Intelligence
                  </h4>
                  <div className="space-y-3">
                    {brief.company_intelligence.map((intel, i) => (
                      <div key={i} className="bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-indigo-300">{intel.name}</span>
                          {intel.website && (
                            <a 
                              href={intel.website.startsWith('http') ? intel.website : `https://${intel.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Website
                            </a>
                          )}
                        </div>
                        {intel.overview && (
                          <p className="text-xs text-gray-400 mb-2">{intel.overview}</p>
                        )}
                        {intel.recent_news?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <span className="text-xs text-gray-500 block mb-1">Recent News:</span>
                            {intel.recent_news.slice(0, 2).map((news, j) => (
                              <div key={j} className="text-xs text-gray-400 mb-1">
                                • {news.title}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Attendees (collapsed section) */}
              {brief.attendees?.length > 0 && (
                <div>
                  <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Attendees
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {brief.attendees.map((attendee, i) => (
                      <div key={i} className="flex items-center gap-2 bg-white/5 rounded-lg px-2 py-1">
                        <span className="text-xs text-gray-300">{attendee.name || attendee.email}</span>
                        {attendee.company && (
                          <span className="text-xs text-gray-500">@ {attendee.company}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Meeting Link */}
              {brief.meeting_links?.length > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <Button
                    onClick={() => window.open(brief.meeting_links[0].url, '_blank')}
                    size="sm"
                    className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white text-xs rounded-lg"
                  >
                    <Video className="h-3 w-3 mr-1" />
                    Join Meeting
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PrioritiesSection;
